import { useEffect, useState } from 'react';
import { AlertTriangle, Keyboard, Loader2, X } from 'lucide-react';
import type { ProjectReader } from '../lib/types';
import type { ReaderExtraction } from '../services/readings';
import { runReaderExtraction } from '../services/readings';
import { createCapture, newCaptureId, setCaptureFailed } from '../services/captures';
import { readerSnapshot } from '../services/readers';
import { useAuth } from '../hooks/useAuth';
import { Button } from './ui';
import ReaderConfirmation from './ReaderConfirmation';

/**
 * Typed and spoken entry for a project that records against readers.
 *
 * Photo reads already land on the reader confirmation screen. This brings the
 * other two modes to the same place, so whichever way a number arrives it is
 * checked against the same fields and saved as the same kind of reading.
 */
export type ReaderSession =
  | { kind: 'manual'; reader: ProjectReader }
  | { kind: 'voice'; reader: ProjectReader; captureId: string; transcript: string }
  /** Readings already read and kept on a capture — a reader's trial photo. */
  | { kind: 'stored'; reader: ProjectReader; captureId: string; extraction: ReaderExtraction };

function blankEntry(reader: ProjectReader): ReaderExtraction {
  return {
    readerId: reader.id,
    reader: { id: reader.id, name: reader.name, fields: reader.fields },
    orientation: 'samples-in-rows',
    confidence: 'high',
    columnsSeen: [],
    readings: [{ sampleLabel: 'Sample 1', labelParts: ['Sample 1'], replicate: 1, values: {} }],
    unreadable: [],
  };
}

export default function ReaderCapture({
  projectId,
  session,
  onClose,
}: {
  projectId: string;
  session: ReaderSession;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const uid = user?.uid ?? '';
  const [current, setCurrent] = useState<ReaderSession>(session);
  const [extraction, setExtraction] = useState<ReaderExtraction | null>(
    session.kind === 'manual'
      ? blankEntry(session.reader)
      : session.kind === 'stored'
        ? session.extraction
        : null,
  );
  const [error, setError] = useState<string | null>(null);

  // A dictation is read once, when it arrives.
  useEffect(() => {
    if (session.kind !== 'voice') return;
    let cancelled = false;
    runReaderExtraction({
      projectId,
      captureId: session.captureId,
      readerId: session.reader.id,
      spokenText: session.transcript,
    })
      .then((result) => {
        if (!cancelled) setExtraction(result);
      })
      .catch((e: Error) => {
        const message = e?.message ?? 'Nothing could be read from that.';
        void setCaptureFailed(projectId, session.captureId, message).catch(() => {});
        if (!cancelled) setError(message);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, session]);

  function typeInstead() {
    setError(null);
    setCurrent({ kind: 'manual', reader: current.reader });
    setExtraction(blankEntry(current.reader));
  }

  const manual = current.kind === 'manual';

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-ink-950" data-confirming="true">
      <header
        className="flex items-center gap-3 border-b border-ink-800 px-4 pb-3"
        style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
      >
        <div className="min-w-0">
          <h2 className="truncate text-sm font-semibold">
            {manual ? 'Enter readings' : 'Confirm readings'}
          </h2>
          <p className="truncate text-xs text-ink-400">{current.reader.name}</p>
        </div>
        <button
          onClick={onClose}
          className="ml-auto rounded-lg p-2 text-ink-400 hover:bg-ink-800 hover:text-ink-100"
          aria-label="Close without saving"
        >
          <X size={18} />
        </button>
      </header>

      {current.kind === 'voice' && (
        <p className="border-b border-ink-800 px-4 py-2 font-mono text-xs text-ink-300">
          “{current.transcript}”
        </p>
      )}

      {error ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-6 text-center">
          <p className="flex max-w-sm gap-2 rounded-lg border border-amber-800 bg-amber-950 p-3 text-left text-sm text-amber-200">
            <AlertTriangle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </p>
          <Button variant="secondary" icon={<Keyboard size={16} />} onClick={typeInstead}>
            Type the values instead
          </Button>
        </div>
      ) : !extraction ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-sm text-ink-400">
          <Loader2 size={22} className="animate-spin text-accent-400" />
          Reading what you said…
        </div>
      ) : (
        <ReaderConfirmation
          // Remounted when switching from a failed dictation to typing.
          key={current.kind}
          projectId={projectId}
          captureId={current.kind === 'manual' ? null : current.captureId}
          uid={uid}
          extraction={extraction}
          manual={manual}
          resolveCaptureId={async () => {
            const id = newCaptureId(projectId);
            await createCapture(projectId, id, {
              mode: 'manual',
              instrument: 'unknown',
              capturedByUid: uid,
              capturedByName: user?.displayName ?? user?.email ?? null,
              reader: readerSnapshot(current.reader),
            });
            return id;
          }}
          onDone={onClose}
          onCancel={onClose}
        />
      )}
    </div>
  );
}
